
import React from "react";
import { Button } from "@/components/ui/button";
import { 
  Dialog, 
  DialogContent, 
  DialogTitle, 
  DialogHeader,
  DialogDescription,
  DialogTrigger
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, Crown } from "lucide-react";
import StaffForm from "./StaffForm";
import { useStaffActions } from "@/hooks/useStaffActions";

interface AddStaffDialogProps {
  onStaffAdded: () => void;
  setEditingStaff?: (staff: any) => void;
  setIsEditStaffDialogOpen?: (isOpen: boolean) => void;
  disabled?: boolean;
  upgradeMessage?: string;
}

const AddStaffDialog: React.FC<AddStaffDialogProps> = ({ onStaffAdded, setEditingStaff, setIsEditStaffDialogOpen, disabled = false, upgradeMessage }) => {
  const [open, setOpen] = React.useState(false);
  const { handleStaffAdded, isLoading } = useStaffActions(onStaffAdded, setEditingStaff, setIsEditStaffDialogOpen);

  const handleClose = (refresh: boolean = false) => {
    setOpen(false);
    if (refresh) {
      onStaffAdded();
    }
  };

  const handleSubmit = async (staffData: any, continueToDetails?: boolean) => {
    try {
      await handleStaffAdded(staffData, continueToDetails);
      setOpen(false);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  const buttonContent = (
    <Button 
      className="flex gap-2" 
      disabled={disabled}
      variant={disabled ? "outline" : "default"}
    >
      {disabled ? <Crown size={16} /> : <Plus size={16} />}
      <span>{disabled ? "Upgrade for More Users" : "Add Team Member"}</span>
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={disabled ? undefined : setOpen}>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <DialogTrigger asChild disabled={disabled}>
              {buttonContent}
            </DialogTrigger>
          </TooltipTrigger>
          {disabled && upgradeMessage && (
            <TooltipContent>
              <p className="max-w-xs">{upgradeMessage}</p>
            </TooltipContent>
          )}
        </Tooltip>
      </TooltipProvider>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Staff Member</DialogTitle>
          <DialogDescription>
            Create a new staff member for your organization
          </DialogDescription>
        </DialogHeader>
        <StaffForm onClose={handleClose} onSubmit={handleSubmit} />
      </DialogContent>
    </Dialog>
  );
};

export default AddStaffDialog;
